import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PortalBranding {
  logo: string | null;
  primaryColor: string | null;
  practiceName: string | null;
}

export interface PortalPatientInfo {
  patientId: number;
  accountId: number;
  firstName: string;
  lastName: string;
  email: string;
}

interface PortalAuthState {
  token: string | null;
  patientId: number | null;
  accountId: number | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  branding: PortalBranding;
}

interface PortalAuthActions {
  login: (token: string, patient: PortalPatientInfo, branding: PortalBranding) => void;
  logout: () => void;
}

export type PortalAuthStore = PortalAuthState & PortalAuthActions;

// ─── Initial state ────────────────────────────────────────────────────────────

const initialState: PortalAuthState = {
  token: null,
  patientId: null,
  accountId: null,
  firstName: null,
  lastName: null,
  email: null,
  branding: {
    logo: null,
    primaryColor: null,
    practiceName: null,
  },
};

// ─── Store ────────────────────────────────────────────────────────────────────

export const usePortalAuthStore = create<PortalAuthStore>()(
  persist(
    (set) => ({
      ...initialState,

      login: (token, patient, branding) =>
        set({
          token,
          patientId: patient.patientId,
          accountId: patient.accountId,
          firstName: patient.firstName,
          lastName: patient.lastName,
          email: patient.email,
          branding,
        }),

      logout: () => {
        // Clear the token from localStorage explicitly before resetting state
        localStorage.removeItem('ftp-portal-auth');
        set(initialState);
      },
    }),
    {
      name: 'ftp-portal-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        token: state.token,
        patientId: state.patientId,
        accountId: state.accountId,
        firstName: state.firstName,
        lastName: state.lastName,
        email: state.email,
        branding: state.branding,
      }),
    },
  ),
);
